import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import { Lightbox } from "../components/site/Lightbox";
import { StoneCard, AvailDot } from "../components/site/StoneCard";
import { Btn } from "../components/site/Btn";
import { FadeUp, Lines } from "../components/site/Reveal";
import { useSeo } from "../components/site/Seo";
import { imgSrc } from "../lib/api";
import { useLang, stoneName, stoneText, stoneDims, fmt } from "../i18n/lang";
import { useMeta, useStone, useStones } from "../lib/queries";

const Spec = ({ label, value, testid }) => value ? (
  <div className="grid grid-cols-5 gap-4 py-4 border-b border-[#1A1A1A]/12" data-testid={testid}>
    <dt className="col-span-2 eyebrow !text-[0.66rem] text-[#555555] pt-0.5">{label}</dt>
    <dd className="col-span-3 text-[0.95rem] whitespace-pre-line">{value}</dd>
  </div>
) : null;

export default function StoneDetail() {
  const { code } = useParams();
  const { t, to, lang } = useLang();
  const { data: stone, isLoading } = useStone(code);
  const { data: meta } = useMeta();
  const { data: all = [] } = useStones();
  const [active, setActive] = useState(0);
  const [box, setBox] = useState(false);
  const name = stone ? stoneName(stone, lang) : "";
  useSeo(stone ? `${name} (${stone.code}) — THE STONE EDITION` : "THE STONE EDITION", stone ? `${name} — ${t.form[stone.form]}, ${stoneDims(stone) || ""}. ${t.selection.sub}` : "");

  if (isLoading) return <main className="min-h-screen pt-48 px-6 f-mono text-xs" data-testid="stone-loading">{t.common.loading}…</main>;
  if (!stone) return (
    <main className="min-h-[80vh] pt-48 px-6 sm:px-10 lg:px-14" data-testid="stone-not-found">
      <h1 className="f-serif text-5xl font-light">{t.detail.notFound}</h1>
      <div className="mt-10"><Btn to={to("/stones")} variant="outline">{t.detail.toArchive}</Btn></div>
    </main>
  );

  const images = stone.images?.length ? stone.images : [];
  const cat = (meta?.categories || []).find((c) => c.slug === stone.category);
  const catName = cat ? (lang === "it" ? cat.name_it : cat.name_en) : stone.category;
  const others = all.filter((s) => s.id !== stone.id && s.availability === "available").slice(0, 3);
  const canEnquire = stone.availability !== "sold";

  return (
    <main data-testid="stone-detail-page">
      <section className="px-6 sm:px-10 lg:px-14 pt-32 sm:pt-36 pb-24">
        <nav className="f-mono text-[0.7rem] tracking-[0.1em] text-[#555555] mb-10" aria-label="Breadcrumb">
          <Link to={to("/stones")} className="link-line" data-testid="breadcrumb-archive">{t.archive.title}</Link><span className="mx-3">/</span><span>{stone.code}</span>
        </nav>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <button type="button" onClick={() => images.length && setBox(true)} data-testid="stone-main-image" aria-label={t.detail.zoom}
              className="group relative block w-full aspect-[4/5] bg-[#F0F0F0] overflow-hidden cursor-zoom-in">
              <motion.img key={active} src={imgSrc(images[active])} alt={`${name} — ${stone.code}`}
                className="absolute inset-0 w-full h-full object-contain" initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }} />
              <span className="absolute bottom-4 right-4 eyebrow !text-[0.62rem] bg-[#FFFFFF] px-3 py-2 flex items-center gap-2"><Maximize2 className="w-3 h-3" strokeWidth={1.5} />{t.detail.zoom}</span>
            </button>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="stone-thumbs">
                {images.map((src, i) => (
                  <button key={src + i} type="button" onClick={() => setActive(i)} data-testid={`stone-thumb-${i}`} aria-label={`Image ${i + 1}`}
                    className={`aspect-square overflow-hidden bg-[#F0F0F0] transition-opacity duration-300 ${active === i ? "opacity-100 outline outline-1 outline-[#1A1A1A] outline-offset-2" : "opacity-60 hover:opacity-100"}`}>
                    <img src={imgSrc(src)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="f-mono text-xs tracking-[0.12em] border border-[#1A1A1A]/40 px-2 py-1" data-testid="stone-code">{stone.code}</span>
                {stone.is_demo && <span className="eyebrow !text-[0.62rem] bg-[#1A1A1A] text-[#FFFFFF] px-2 py-1">{t.common.example}</span>}
                <span className="text-sm ml-auto"><AvailDot value={stone.availability} t={t} /></span>
              </div>
              <Lines as="h1" onMount lines={[name]} className="f-serif font-light tracking-[-0.02em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl mt-8" testid="stone-name" />
              <p className="mt-4 text-[#555555]">{catName} · {t.form[stone.form]}</p>
              <dl className="mt-10 border-t border-[#1A1A1A]/12">
                <Spec label={t.detail.type} value={catName} testid="spec-type" />
                <Spec label={t.detail.format} value={t.form[stone.form]} testid="spec-format" />
                <Spec label={`${t.detail.dims} (${stone.form === "block" ? t.detail.dimsBlock : t.detail.dimsSlab})`} value={stoneDims(stone)} testid="spec-dims" />
                {stone.form === "slab" && <Spec label={t.detail.thickness} value={fmt(stone.thickness_cm) && `${fmt(stone.thickness_cm)} cm`} testid="spec-thickness" />}
                <Spec label={t.detail.finish} value={stone.finish && t.finishes[stone.finish]} testid="spec-finish" />
                <Spec label={t.detail.color} value={stone.color && t.colors[stone.color]} testid="spec-color" />
                <Spec label={t.detail.origin} value={stone.origin} testid="spec-origin" />
                <Spec label={t.detail.tech} value={stoneText(stone, "tech", lang)} testid="spec-tech" />
              </dl>
              {stoneText(stone, "notes", lang) && (
                <div className="mt-8"><p className="eyebrow !text-[0.66rem] text-[#555555]">{t.detail.notes}</p><p className="mt-3 leading-relaxed whitespace-pre-line">{stoneText(stone, "notes", lang)}</p></div>
              )}
              {stone.is_demo && <p className="mt-6 text-sm bg-[#F0F0F0] p-4" data-testid="demo-notice">{t.detail.demo}</p>}
              <div className="mt-10" data-testid="stone-cta-block">
                {canEnquire ? (
                  <>
                    {stone.availability === "reserved" && <p className="text-sm text-[#555555] mb-4">{t.detail.reservedNote}</p>}
                    <Btn to={`${to("/start-a-project")}?stone=${encodeURIComponent(stone.code)}`} className="w-full" testid="enquire-stone-btn">{t.detail.enquire}</Btn>
                  </>
                ) : <p className="border border-[#1A1A1A]/20 p-4 text-sm" data-testid="stone-unavailable">{t.detail.unavailable}</p>}
              </div>
              <p className="mt-8 text-xs leading-relaxed text-[#555555]" data-testid="stone-disclaimer">{t.detail.disclaimer}</p>
            </div>
          </aside>
        </div>
      </section>
      {others.length > 0 && (
        <section className="px-6 sm:px-10 lg:px-14 py-24 border-t border-[#1A1A1A]/12">
          <h2 className="f-serif text-4xl font-light mb-12">{t.detail.more}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {others.map((s, i) => <FadeUp key={s.id} delay={i * 0.08}><StoneCard stone={s} cats={meta?.categories || []} /></FadeUp>)}
          </div>
        </section>
      )}
      {box && <Lightbox images={images} index={active} onIndex={setActive} onClose={() => setBox(false)} alt={name} />}
    </main>
  );
}
